import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { getBlob, getStorage, ref } from '@angular/fire/storage';

import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  localStorage: Storage;
  storage = getStorage();

  constructor(
    private http: HttpClient,
    private logger: LoggerService
  ) {
    this.localStorage = window.localStorage;
  }

  get isLocalStorageSupported(): boolean {
    return !!this.localStorage;
  }

  get(key: string): any {
    if (this.isLocalStorageSupported) {
      let item = this.localStorage.getItem(key);
      if (item === 'undefined') {
        item = undefined;
      } else {
        this.logger.debug('Sto:GetCache:Ok:' + key);
        try {
          item = JSON.parse(item);
        } catch (err) {}
      }

      return item;
    }

    return undefined;
  }

  set(key: string, value: object | string): boolean {
    if (this.isLocalStorageSupported) {
      if (typeof value == 'object') {
        this.localStorage.setItem(key, JSON.stringify(value));
      } else {
        this.localStorage.setItem(key, value);
      }
      this.logger.debug('Sto:SetCache:' + key);

      return true;
    }

    return false;
  }

  remove(key: string): boolean {
    if (this.isLocalStorageSupported) {
      this.localStorage.removeItem(key);

      return true;
    }

    return false;
  }

  downloadStorage(path: string): Promise<object | null> {
    this.logger.debug(`Sto:downloadStorage:${path}`);

    return new Promise((resolve, reject) => {
      return getBlob(ref(this.storage, path))
        .then(blob => {
          return blob
            .text()
            .then(content => {
              this.logger.debug(`Sto:downloadStorage:Size: ${blob.size}`);
              this.logger.debug({ 'Sto:downloadStorage:Content': content });
              resolve(JSON.parse(content));
            })
            .catch(err => {
              this.logger.error({ 'Sto:downloadStorage:Get': err });
            });
        })
        .catch(err => {
          this.logger.debug({ 'Sto:downloadStorage': err });
          reject(err);
        });
    });
  }

  async getMediaLocal(key: string, urlRef: string): Promise<any> {
    let cachedItem = undefined;
    const cachedCode = this.get(key + '_code');
    this.logger.debug('Sto:GetUrl:' + urlRef);
    const newCode = urlRef.split('/').at(-1).split('.')[0];
    this.logger.debug('Sto:GetUrl:Code:' + newCode);
    if (cachedCode === newCode) {
      cachedItem = this.get(key);
    }
    if (cachedItem == undefined) {
      await this.http.get(urlRef).forEach(newItem => {
        this.set(key, newItem);
        this.set(key + '_code', newCode);
        cachedItem = newItem;
      });
    }

    return cachedItem;
  }

  needRefresh(key: string, newCode: string): boolean {
    const cachedCode = this.get(key + '_code');
    return newCode !== cachedCode;
  }

  async getStorageLocal(key: string, newCode: string, path: string): Promise<object | null> {
    const cachedCode = this.get(key + '_code');
    this.logger.debug(`Sto:GetSto:Key:${key}`);

    return new Promise((resolve, reject) => {
      if (newCode === cachedCode) {
        const content = this.get(key);
        this.logger.debug({ 'Sto:GetSto:Cached': content });
        resolve(content);
      } else {
        this.logger.debug(`Sto:GetSto:Path:${newCode}: ${path}`);

        return this.downloadStorage(path)
          .then(content => {
            if (content !== null) {
              this.logger.debug({ 'Sto:GetSto:Download': content });
              this.set(key, content);
              this.set(key + '_code', newCode);
            }
            resolve(content);
          })
          .catch(err => {
            this.logger.error({ 'Sto:GetSto': err });
            reject(err);
          });
      }
    });
  }
}
